import React from 'react';
import { Circle, Square, Triangle, Users, Handshake, MessageCircle, Hexagon } from 'lucide-react';

/**
 * LOGO PROPOSALS FOR ONE TABLE MEDIATION
 * 
 * Concept: "One Table" - The idea of bringing parties together around a single table
 * Theme: Unity, collaboration, resolution, neutrality
 */

// Logo Option 1: Round Table Icon
export const LogoOption1 = ({ className = "h-8 w-8" }) => (
  <div className="flex items-center gap-2">
    <div className="relative">
      <Circle className={`${className} text-navy-600`} strokeWidth={2.5} />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-1 h-1 bg-navy-600 rounded-full"></div>
      </div>
    </div>
  </div>
);

// Logo Option 2: Square Table with People
export const LogoOption2 = ({ className = "h-8 w-8" }) => (
  <div className="relative inline-block">
    <Square className={`${className} text-navy-600`} strokeWidth={2} />
    <Users className="absolute inset-0 m-auto h-4 w-4 text-navy-600" strokeWidth={2} />
  </div>
);

// Logo Option 3: Handshake Symbol
export const LogoOption3 = ({ className = "h-8 w-8" }) => (
  <Handshake className={`${className} text-navy-600`} strokeWidth={2} />
);

// Logo Option 4: Three Circles (parties) Meeting at One Point
export const LogoOption4 = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    <circle cx="30" cy="40" r="15" stroke="currentColor" strokeWidth="3" className="text-navy-600" />
    <circle cx="70" cy="40" r="15" stroke="currentColor" strokeWidth="3" className="text-navy-600" />
    <circle cx="50" cy="70" r="15" stroke="currentColor" strokeWidth="3" className="text-navy-600" />
    <circle cx="50" cy="50" r="5" fill="currentColor" className="text-navy-600" />
  </svg>
);

// Logo Option 5: Hexagon Table (geometric, modern)
export const LogoOption5 = ({ className = "h-8 w-8" }) => (
  <Hexagon className={`${className} text-navy-600`} strokeWidth={2.5} />
);

// Logo Option 6: Overlapping Circles (Venn diagram style - finding common ground)
export const LogoOption6 = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    <circle cx="40" cy="50" r="25" stroke="currentColor" strokeWidth="3" className="text-navy-600" opacity="0.7" />
    <circle cx="60" cy="50" r="25" stroke="currentColor" strokeWidth="3" className="text-navy-600" opacity="0.7" />
    <path d="M 50 30 L 50 70" stroke="currentColor" strokeWidth="2" className="text-navy-800" />
  </svg>
);

// Logo Option 7: Triangle Table (stability, balance)
export const LogoOption7 = ({ className = "h-8 w-8" }) => (
  <div className="relative inline-block">
    <Triangle className={`${className} text-navy-600`} strokeWidth={2.5} />
    <div className="absolute inset-0 flex items-center justify-center pt-1">
      <div className="w-1 h-1 bg-navy-600 rounded-full"></div>
    </div>
  </div>
);

// Logo Option 8: Minimalist "1" with Table Line
export const LogoOption8 = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    <text x="35" y="65" fontSize="60" fontWeight="bold" fill="currentColor" className="text-navy-600">1</text>
    <line x1="20" y1="85" x2="80" y2="85" stroke="currentColor" strokeWidth="4" className="text-navy-600" />
  </svg>
);

// Logo Option 9: Conversation Bubbles Meeting
export const LogoOption9 = ({ className = "h-8 w-8" }) => (
  <div className="relative">
    <MessageCircle className={`${className} text-navy-600`} strokeWidth={2} />
    <div className="absolute -top-1 -right-1 w-3 h-3">
      <MessageCircle className="w-full h-full text-navy-400" strokeWidth={2} />
    </div>
  </div>
);

// Logo Option 10: Custom Table Shape
export const LogoOption10 = ({ className = "h-8 w-8" }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none">
    {/* Table top */}
    <rect x="20" y="35" width="60" height="8" fill="currentColor" className="text-navy-600" rx="2" />
    {/* Table legs */}
    <rect x="25" y="43" width="4" height="30" fill="currentColor" className="text-navy-600" />
    <rect x="71" y="43" width="4" height="30" fill="currentColor" className="text-navy-600" />
    {/* People around table (dots) */}
    <circle cx="30" cy="25" r="3" fill="currentColor" className="text-navy-400" />
    <circle cx="50" cy="25" r="3" fill="currentColor" className="text-navy-400" />
    <circle cx="70" cy="25" r="3" fill="currentColor" className="text-navy-400" />
  </svg>
);

/**
 * LOGO DESCRIPTIONS & RECOMMENDATIONS:
 * 
 * Option 1 - Round Table: Classic, symbolizes equality and inclusivity
 * Option 2 - Square Table with People: Direct representation of mediation
 * Option 3 - Handshake: Universal symbol of agreement and resolution
 * Option 4 - Three Circles Meeting: Represents multiple parties finding common ground
 * Option 5 - Hexagon: Modern, geometric, stable - represents structure
 * Option 6 - Overlapping Circles: Venn diagram style, finding intersection/agreement
 * Option 7 - Triangle: Symbolizes stability and balance in mediation
 * Option 8 - "1" with Table: Direct reference to "One Table", minimalist
 * Option 9 - Conversation Bubbles: Represents dialogue and communication
 * Option 10 - Table with People: Literal representation of bringing people together
 * 
 * RECOMMENDED: Options 4, 6, or 8 for their unique representation of the "One Table" concept
 */

export const logoOptions = [
  { id: 1, name: "Round Table", component: LogoOption1, description: "Classic circle - equality & inclusivity" },
  { id: 2, name: "Table & People", component: LogoOption2, description: "Direct mediation symbol" },
  { id: 3, name: "Handshake", component: LogoOption3, description: "Universal agreement symbol" },
  { id: 4, name: "Convergence", component: LogoOption4, description: "Parties meeting at common ground - RECOMMENDED" },
  { id: 5, name: "Hexagon", component: LogoOption5, description: "Modern geometric stability" },
  { id: 6, name: "Common Ground", component: LogoOption6, description: "Venn intersection - RECOMMENDED" },
  { id: 7, name: "Triangle Balance", component: LogoOption7, description: "Stability & balance symbol" },
  { id: 8, name: "One Table Minimalist", component: LogoOption8, description: "Literal '1' with table - RECOMMENDED" },
  { id: 9, name: "Dialogue", component: LogoOption9, description: "Communication bubbles" },
  { id: 10, name: "Table Scene", component: LogoOption10, description: "Literal table with people" }
];
